import { z } from 'zod';
import { DraftStepSchema } from './booking.js';
import { DecisionMessageKeySchema, InsuranceTypeSchema } from './eligibility-rules.js';
import { MetricDefinitionSchema, ReportTableSchema } from './reports.js';
import { LocalDateSchema, StateCodeSchema } from './scheduling.js';
/**
 * First-party funnel analytics (M5.8, ADR-019, FR-BOOK-14/15, DATABASE.md §7.11).
 *
 * ## Why the shapes here are closed rather than open
 *
 * ADR-019 exists because the live site's third-party tags are the primary HIPAA
 * finding: a pixel on a page whose URL said `?name=…&dob=…` shipped patient data to a
 * vendor. The replacement is our own table - but an analytics table with a free-form
 * `properties` object is the same failure with a different hostname, because the first
 * person in a hurry will put the email in it "just for debugging".
 *
 * So both halves are allow-lists:
 *
 * - **`FUNNEL_EVENT_NAMES`** is closed. A name outside it is a `400`, not a row.
 * - **`FunnelEventPropertiesSchema`** is `.strict()` and every field in it is a
 *   category, a count or a flag. There is no string field a patient could type into.
 *
 * `state_unavailable` is the one event that carries anything about the person, and it
 * carries a two-letter state and nothing else (FR-BOOK-03: "record an anonymous
 * interest event (state only)"). That is enforced here, in `FunnelEventSchema`'s
 * refinement, rather than trusted to the caller.
 */
/* ---------------------------------------------------------------------- names ---- */
/**
 * What the browser may report. One name per thing the patient actually did, in funnel
 * order, so the report can walk the list and compute drop-off without a mapping table.
 */
export const FUNNEL_EVENT_NAMES = [
    /** The funnel was opened. One per draft; the denominator of every conversion rate. */
    'funnel_started',
    'state_selected',
    /** FR-BOOK-03: an unsupported state. Carries the state code and nothing else. */
    'state_unavailable',
    'type_selected',
    'date_selected',
    'slot_selected',
    'details_completed',
    'insurance_completed',
    'card_saved',
    /** The patient pressed confirm. The *server's* record of it is `booking_confirmed`. */
    'confirmed',
    /** Sent on unload/blur when a step was left incomplete. Best effort, never trusted. */
    'abandoned',
    /** The resume prompt was accepted (FR-BOOK-10's numerator). */
    'resumed',
];
export const FunnelEventNameSchema = z.enum(FUNNEL_EVENT_NAMES);
/**
 * Written by the server, never accepted from a client.
 *
 * `confirmed` is what the browser claims happened; `booking_confirmed` is what the
 * booking transaction knows happened. The report counts the second one, which is why a
 * client that never fires `confirmed` - a closed tab, a blocked request - cannot make
 * the conversion rate look worse than it is.
 */
/** `booking_blocked` (M15): the decision engine stopped a draft. State, plan type, key; nothing else. */
export const SERVER_EVENT_NAMES = ['booking_confirmed', 'booking_blocked'];
/** Everything `analytics_events.event_name` may hold. Mirrors `ck_analytics_events_name`. */
export const ANALYTICS_EVENT_NAMES = [...FUNNEL_EVENT_NAMES, ...SERVER_EVENT_NAMES];
const FUNNEL_EVENT_NAME_SET = new Set(FUNNEL_EVENT_NAMES);
/** Narrows a name read from a request body or a database row. */
export function isFunnelEventName(value) {
    return FUNNEL_EVENT_NAME_SET.has(value);
}
/* ----------------------------------------------------------------- properties ---- */
/**
 * The whole vocabulary of `analytics_events.properties`.
 *
 * Categories, counts and flags. No name, no email, no phone, no date of birth, no
 * member id, no free text - and `.strict()` means a payload carrying one is refused
 * rather than trimmed, so a client that starts sending PHI fails loudly in its own
 * tests instead of silently writing it for two years.
 */
export const FunnelEventPropertiesSchema = z
    .object({
    /** Two-letter state. The only field `state_unavailable` may carry. */
    state: StateCodeSchema.optional(),
    /** The visit type's key, never its uuid: the report groups on something readable. */
    appointmentTypeKey: z.string().min(1).max(64).optional(),
    /** Self-pay or insurance, as an intent. Not a payer, not a member id. */
    insured: z.boolean().optional(),
    /** Time on the step, for the report's "where do they stall" column. */
    durationMs: z.number().int().min(0).max(86_400_000).optional(),
    /** Why a step ended: `back`, `timeout`, `slot_taken`, `closed`. A closed set. */
    reason: z.enum(['back', 'timeout', 'slot_taken', 'closed', 'error', 'other']).optional(),
    /** M15 `booking_blocked`: the declared plan type (a category, not a payer). */
    insuranceType: InsuranceTypeSchema.optional(),
    /** M15 `booking_blocked`: which fixed sentence the patient was shown. */
    messageKey: DecisionMessageKeySchema.optional(),
    /** M15 `booking_blocked`: the scheduling ruleset version that decided. */
    rulesetVersion: z.int().nonnegative().optional(),
})
    .strict();
/* --------------------------------------------------------------------- events ---- */
export const FunnelEventSchema = z
    .object({
    name: FunnelEventNameSchema,
    /** Which step it happened on. Null for events that are not on a step. */
    step: DraftStepSchema.nullable().optional(),
    /**
     * The draft this belongs to. Optional because `funnel_started` and
     * `state_unavailable` can precede a draft; when present it must be the draft the
     * caller's own cookie owns, and the server refuses any other.
     */
    draftId: z.uuid().nullable().optional(),
    /** Improvement variants active for this visitor, so the report can split on them. */
    variants: z.array(z.string().min(1).max(64)).max(32).default([]),
    properties: FunnelEventPropertiesSchema.default({}),
    /**
     * When the browser thinks it happened. Clamped server-side to
     * `[now - 24 h, now]`: a clock that is wrong by a year must not put a row outside
     * every report's range, and a client must not be able to backdate one.
     */
    occurredAt: z.iso.datetime({ offset: true }).optional(),
})
    .strict()
    .refine((event) => event.name !== 'state_unavailable' ||
    Object.keys(event.properties).every((key) => key === 'state'), {
    message: 'state_unavailable carries the state and nothing else (FR-BOOK-03).',
    path: ['properties'],
});
/**
 * `POST /public/events`. A batch, because the funnel fires several events per step and
 * one request per event would spend the visitor's rate-limit budget on bookkeeping.
 */
export const FunnelEventBatchSchema = z
    .object({
    events: z.array(FunnelEventSchema).min(1).max(50),
})
    .strict();
/** `202`. The count is what was stored, which may be fewer than what was sent. */
export const FunnelEventBatchResponseSchema = z.object({
    data: z.object({ accepted: z.number().int().min(0) }),
});
/* --------------------------------------------------------------------- report ---- */
export const FunnelGroupBySchema = z.enum(['day', 'week']);
/** `GET /admin/reports/funnel`. `from` inclusive, `to` exclusive (API.md §1.7). */
export const FunnelReportQuerySchema = z
    .object({
    from: LocalDateSchema,
    to: LocalDateSchema,
    groupBy: FunnelGroupBySchema.default('day'),
    /** Narrow to one visit type, by key. */
    appointmentTypeKey: z.string().min(1).max(64).optional(),
    state: StateCodeSchema.optional(),
    /** `utm_source` captured when the draft was created; `direct` when there was none. */
    source: z.string().min(1).max(64).optional(),
})
    .refine((value) => value.from <= value.to, {
    message: 'The range must end after it begins.',
    path: ['to'],
});
/**
 * One row of the funnel itself.
 *
 * `drafts` counts drafts that *reached* the step; `sessions` counts the browser events
 * reported for it. They differ on purpose: a client that drops an event still has a
 * draft, so `drafts` is the number the clinic should trust and `sessions` is the one
 * that shows whether the instrumentation is healthy.
 */
export const FunnelStepRowSchema = z.object({
    step: DraftStepSchema,
    sessions: z.number().int().min(0),
    drafts: z.number().int().min(0),
    /** Share of starts that reached this step, 0..1, rounded to four places. */
    conversionFromStart: z.number().min(0).max(1),
    /** Share of the previous step's drafts that did not reach this one, 0..1. */
    dropOff: z.number().min(0).max(1),
});
export const FunnelBreakdownRowSchema = z.object({
    /** The state code, the visit type key or the source. `unknown` when unattributed. */
    key: z.string(),
    starts: z.number().int().min(0),
    confirmations: z.number().int().min(0),
    conversionRate: z.number().min(0).max(1),
});
export const FunnelSeriesPointSchema = z.object({
    /** `YYYY-MM-DD`: the day, or the Monday of the week. */
    bucket: LocalDateSchema,
    starts: z.number().int().min(0),
    confirmations: z.number().int().min(0),
    conversionRate: z.number().min(0).max(1),
});
export const FunnelReportSchema = z.object({
    range: z.object({
        from: LocalDateSchema,
        to: LocalDateSchema,
        groupBy: FunnelGroupBySchema,
        /** The clinic zone the days are bucketed in, so a chart can say so. */
        timeZone: z.string(),
    }),
    totals: z.object({
        starts: z.number().int().min(0),
        confirmations: z.number().int().min(0),
        abandoned: z.number().int().min(0),
        resumePrompts: z.number().int().min(0),
        resumes: z.number().int().min(0),
        /** confirmations ÷ starts. */
        conversionRate: z.number().min(0).max(1),
        /** resumes ÷ abandoned drafts (FR-BOOK-10). */
        resumeRate: z.number().min(0).max(1),
        /** FR-BOOK-03: how many people asked for a state the clinic does not serve. */
        stateUnavailable: z.number().int().min(0),
    }),
    steps: z.array(FunnelStepRowSchema),
    byState: z.array(FunnelBreakdownRowSchema),
    byType: z.array(FunnelBreakdownRowSchema),
    bySource: z.array(FunnelBreakdownRowSchema),
    series: z.array(FunnelSeriesPointSchema),
    /* ----------------------------------------------------- added in M11.6 (additive) */
    /**
     * How long a confirmed booking took, from opening the funnel to confirming.
     *
     * Both `null` when nothing in the range converted. The **median** is the honest one: a
     * single visitor who left a tab open overnight moves the average and not the median,
     * and FR-REPORT-01 asks for "average time to book" without saying which - so both are
     * returned and the screen leads with the median.
     */
    timeToBook: z.object({
        averageSeconds: z.number().int().min(0).nullable(),
        medianSeconds: z.number().int().min(0).nullable(),
    }),
    /**
     * Improvement-flag variants active on the drafts in the range (FR-REPORT-01, ADR-008).
     *
     * **Empty when no experiment ran**, which is the normal case - the screen says so
     * rather than drawing an empty chart. `key` is `<flag>:<variant>`, both from the
     * closed variant list a client may report (`FunnelEventSchema.variants`).
     */
    byVariant: z.array(FunnelBreakdownRowSchema),
    /**
     * The written definition of every number above (rules §51.6), and the same generic
     * `{ columns, rows }` blocks the other three reports carry.
     *
     * `tables` exists so that `POST /admin/reports/:report/export` is one function over
     * four reports rather than four CSV writers that drift from their screens
     * (`analytics/reports/report-csv.ts`). It is a second view of the fields above, not a
     * second source of truth: every cell in it is copied from them.
     */
    definitions: z.array(MetricDefinitionSchema),
    tables: z.array(ReportTableSchema),
});
export const FunnelReportResponseSchema = z.object({ data: FunnelReportSchema });
/* ------------------------------------------------------------ conversion ping ---- */
/**
 * The entire payload of the optional server-side conversion ping (ADR-019,
 * FR-BOOK-15): an opaque event id and a number.
 *
 * Not a booking reference, not an appointment id, not an email hash, not a state. The
 * id is random and is stored nowhere the vendor could join it against, so the ping
 * tells GA4 "a conversion worth this much happened" and nothing else. It is defined
 * here, in the contract, so that widening it later is a visible change to a schema a
 * reviewer reads rather than an extra property on a call site.
 */
export const ConversionPingSchema = z
    .object({
    eventId: z.string().min(8).max(64),
    /** Cents, matching the visit type's self-pay price. */
    value: z.number().int().min(0),
    currency: z.string().length(3),
})
    .strict();
//# sourceMappingURL=analytics.js.map